import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../MusicPlayerContext';
import { getCoverImageUrl, hasVideo } from '../utils/musicStreamUrls';
import { isInstrumentalMusic } from '../utils/musicMeta';
import { theme, buttonClass } from '../ui/theme';

const AdvancedSearch = ({ onSearchResults }) => {
  const { i18n, t } = useTranslation();
  const { setCurrentMusic, addToPlaylist, setPlaying } = useMusicPlayer();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  
  const [formData, setFormData] = useState({
    keyword: '',
    language_category: '',
    music_type: '',
    tags: [],
    dateFrom: '',
    dateTo: '',
    releaseDateFrom: '',
    releaseDateTo: '',
    fileFormat: '',
    durationMin: '',
    durationMax: '',
    sortBy: 'latest'
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      // Multiple tag selection
      if (name === 'tag') {
        setFormData(prev => {
          const tags = prev.tags || [];
          if (checked) {
            return { ...prev, tags: [...tags, value] };
          } else {
            return { ...prev, tags: tags.filter(t => t !== value) };
          }
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    
    try {
      const response = await axios.post('/api/music/advanced-search', formData);
      
      if (response.data.success) {
        setResults(response.data.data);
        if (onSearchResults) {
          onSearchResults(response.data.data);
        }
      } else {
        alert(response.data.error || t('hints.searchFailed'));
      }
    } catch (error) {
      console.error('Advanced search error:', error);
      alert(error.response?.data?.error || t('hints.searchFailedRetry'));
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => {
    setFormData({
      keyword: '',
      language_category: '',
      music_type: '',
      tags: [],
      dateFrom: '',
      dateTo: '',
      releaseDateFrom: '',
      releaseDateTo: '',
      fileFormat: '',
      durationMin: '',
      durationMax: '',
      sortBy: 'latest'
    });
    setResults(null);
  };

  const handleMusicSelect = (music) => {
    setCurrentMusic(music);
    setPlaying(true);
    addToPlaylist(music);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentLanguageTitle = (music) => {
    const lang = i18n.language || 'cn';
    switch (lang) {
      case 'cn':
        return music.title_zh_cn || music.title;
      case 'tw':
        return music.title_zh_tw || music.title;
      case 'ja':
        return music.title_ja || music.title;
      case 'en':
        return music.title_en || music.title;
      default:
        return music.title;
    }
  };

  // Common tags (can be fetched from API, hardcoded here for now)
  const commonTags = [
    { value: '流行', translationKey: 'tagsPop' },
    { value: '摇滚', translationKey: 'tagsRock' },
    { value: '民谣', translationKey: 'tagsFolk' },
    { value: '电子', translationKey: 'tagsElectronic' },
    { value: '古典', translationKey: 'tagsClassical' },
    { value: '爵士', translationKey: 'tagsJazz' },
    { value: 'R&B', translationKey: 'tagsRnB' },
    { value: '说唱', translationKey: 'tagsRap' },
    { value: '轻音乐', translationKey: 'tagsLight' },
    { value: '纯音乐', translationKey: 'tagsInstrumental' }
  ];

  return (
    <div className="space-y-4">
      {/* Toggle button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">{t('navigation.advancedSearch')}</h2>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={buttonClass({ variant: 'outline', size: 'xs' })}
        >
          {showAdvanced ? t('advancedSearch.toggleCollapse') : t('advancedSearch.toggleExpand')}{t('advancedSearch.toggleAdvancedOptions')}
        </button>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-4 space-y-3 text-white'].join(' ')}>
        {/* Keyword search */}
        <div>
          <label className="block text-xs font-medium text-white/80 mb-1">
            {t('advancedSearch.keyword')}
          </label>
          <input
            type="text"
            name="keyword"
            value={formData.keyword}
            onChange={handleInputChange}
            placeholder={t('advancedSearch.keywordPlaceholder')}
            className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
          />
        </div>

        {/* Basic filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-white/80 mb-1">
              {t('advancedSearch.languageCategory')}
            </label>
            <select
              name="language_category"
              value={formData.language_category}
              onChange={handleInputChange}
              className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
            >
              <option value="">{t('advancedSearch.all')}</option>
              <option value="cn">{t('advancedSearch.categoryZh')}</option>
              <option value="ja">{t('advancedSearch.categoryJa')}</option>
              <option value="en">{t('advancedSearch.categoryEn')}</option>
              <option value="world">{t('advancedSearch.categoryWorld')}</option>
              <option value="instrumental">{t('advancedSearch.categoryInstrumental')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/80 mb-1">
              {t('advancedSearch.musicType')}
            </label>
            <input
              type="text"
              name="music_type"
              value={formData.music_type}
              onChange={handleInputChange}
              placeholder={t('advancedSearch.musicTypePlaceholder')}
              className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
            />
          </div>
        </div>

        {/* Advanced options */}
        {showAdvanced && (
          <>
            {/* Tag filter */}
            <div>
              <label className="block text-xs font-medium text-white/80 mb-1">
                {t('advancedSearch.tags')}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {commonTags.map(tag => (
                  <label key={tag.value} className="flex items-center">
                    <input
                      type="checkbox"
                      name="tag"
                      value={tag.value}
                      checked={formData.tags.includes(tag.value)}
                      onChange={handleInputChange}
                      className="mr-1 w-3 h-3"
                    />
                    <span className="text-xs text-white/75">{t(`advancedSearch.${tag.translationKey}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.uploadDateFrom')}
                </label>
                <input
                  type="date"
                  name="dateFrom"
                  value={formData.dateFrom}
                  onChange={handleInputChange}
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.uploadDateTo')}
                </label>
                <input
                  type="date"
                  name="dateTo"
                  value={formData.dateTo}
                  onChange={handleInputChange}
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.releaseDateFrom')}
                </label>
                <input
                  type="date"
                  name="releaseDateFrom"
                  value={formData.releaseDateFrom}
                  onChange={handleInputChange}
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.releaseDateTo')}
                </label>
                <input
                  type="date"
                  name="releaseDateTo"
                  value={formData.releaseDateTo}
                  onChange={handleInputChange}
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                />
              </div>
            </div>

            {/* File format and duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.fileFormat')}
                </label>
                <select
                  name="fileFormat"
                  value={formData.fileFormat}
                  onChange={handleInputChange}
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                >
                  <option value="">{t('advancedSearch.all')}</option>
                  <option value="mp3">MP3</option>
                  <option value="wav">WAV</option>
                  <option value="flac">FLAC</option>
                  <option value="ogg">OGG</option>
                  <option value="m4a">M4A</option>
                  <option value="aac">AAC</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.durationMin')}
                </label>
                <input
                  type="number"
                  name="durationMin"
                  value={formData.durationMin}
                  onChange={handleInputChange}
                  min="0"
                  step="0.1"
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/80 mb-1">
                  {t('advancedSearch.durationMax')}
                </label>
                <input
                  type="number"
                  name="durationMax"
                  value={formData.durationMax}
                  onChange={handleInputChange}
                  min="0"
                  step="0.1"
                  className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
                />
              </div>
            </div>
          </>
        )}

        {/* Sort */}
        <div>
          <label className="block text-xs font-medium text-white/80 mb-1">
            {t('advancedSearch.sortBy')}
          </label>
          <select
            name="sortBy"
            value={formData.sortBy}
            onChange={handleInputChange}
            className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
          >
            <option value="latest">{t('advancedSearch.sortLatest')}</option>
            <option value="popular">{t('advancedSearch.sortPopular')}</option>
            <option value="title">{t('advancedSearch.sortTitle')}</option>
            <option value="artist">{t('advancedSearch.sortArtist')}</option>
            <option value="duration">{t('advancedSearch.sortDuration')}</option>
          </select>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={searching}
            className={[buttonClass({ variant: 'outline', size: 'xs' }), 'flex-1'].join(' ')}
          >
            {searching ? t('advancedSearch.searching') : t('advancedSearch.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className={buttonClass({ variant: 'ghost', size: 'xs' })}
          >
            {t('advancedSearch.reset')}
          </button>
        </div>
      </form>

      {/* Search results */}
      {results && (
        <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-4 text-white'].join(' ')}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">
              {t('advancedSearch.resultsTitle', { total: results.total })}
            </h3>
          </div>

          {results.musics.length === 0 ? (
            <p className="text-white/60 text-center py-8">{t('advancedSearch.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.musics.map((music) => (
                <div
                  key={music.id}
                  onClick={() => handleMusicSelect(music)}
                  className={[theme.surfaces.glassItem, 'rounded-lg p-4 cursor-pointer transition-colors'].join(' ')}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      {getCoverImageUrl(music) ? (
                        <img
                          src={getCoverImageUrl(music)}
                          alt={getCurrentLanguageTitle(music)}
                          className="w-16 h-16 rounded object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded bg-white/[0.04] border border-white/10 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white/45" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                          </svg>
                        </div>
                      )}
                      {/* MTV 图标（如果是视频） */}
                      {hasVideo(music) && (
                        <img
                          src="/icons/mtv.png"
                          alt="MTV"
                          className="absolute top-0 right-0 w-5 h-5 object-contain"
                        />
                      )}
                      {/* 纯音乐图标 */}
                      {!hasVideo(music) && isInstrumentalMusic(music) && (
                        <img
                          src="/icons/mcd.png"
                          alt={t('musicList.categoryInstrumental', '纯音乐')}
                          title={t('musicList.categoryInstrumental', '纯音乐')}
                          className="absolute top-0 right-0 w-5 h-5 object-contain"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white truncate">
                        {getCurrentLanguageTitle(music)}
                      </h4>
                      {music.album && (
                        <p className="text-sm text-white/70 truncate">{music.album}</p>
                      )}
                      <p className="text-xs text-white/55 mt-1">
                        {formatDuration(music.duration)} · {t('advancedSearch.playCount', { count: music.play_count || 0 })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;

